import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AblePipe, AblePurePipe } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { TranslateModule } from '@ngx-translate/core';
import { AbsencesModule } from '@wilson/absences';
import { ActivitiesPipesModule } from '@wilson/activities/pipes';
import { AuditTrailComponentsModule } from '@wilson/audit-trail/components';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterModule } from '@wilson/filter';
import { LabelsModule } from '@wilson/labels';
import {
  LocalizedDateTimeFormatPipe,
  LocalizedDayMonthFormatPipe,
  UseLocalizedDateFormatDirective,
} from '@wilson/localization';
import { ActivityTimeTagComponent } from '@wilson/non-domain-specific/activities-helpers/components';
import { LastUpdateStatusComponent } from '@wilson/non-domain-specific/drawer-common';
import { CollapsibleGroupingSectionComponent } from '@wilson/non-domain-specific/groupings';
import {
  DrawerSettingsManagerDirective,
  NzDrawerContentComponent,
  NzDrawerHeaderComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import { PriorityModule } from '@wilson/non-domain-specific/priority';
import { ViewersAvatarsComponent } from '@wilson/non-domain-specific/realtime-collaboration/live-viewers';
import { MoreShiftActionsDropdownComponent } from '@wilson/non-domain-specific/shift-common-actions';
import { OpenFinalStaffButtonComponent } from '@wilson/non-domain-specific/shift-helpers/assignments';
import {
  AcceptShiftButtonComponent,
  ActivityTimePipe,
  CalculateDurationComponent,
  CalculateWorkingHourComponent,
  CanAllShiftBeModifiedPipe,
  CollapsibleActivitiesListComponent,
  CollapsibleShiftAssignedUserIdentityComponent,
  CollapsibleShiftAttachmentsComponent,
  CollapsibleShiftContentComponent,
  CollapsibleShiftDetailComponent,
  CollapsibleShiftFurtherInformationComponent,
  CollapsibleShiftNoteComponent,
  CollapsibleShiftQuickCheckComponent,
  CollapsibleShiftReportsAttachmentsComponent,
  CollapsibleShiftWageDataComponent,
  IsShiftSubmittedOrAcceptedPipe,
  MoveShiftHelperService,
  RevertShiftButtonComponent,
  SendShiftBackToAssigneeButtonComponent,
  SetShiftProjectComponent,
  ShiftFormPublishButtonComponent,
  ShiftNameExistsTooltipComponent,
  ShiftOperationsService,
  ShiftStatusComponent,
} from '@wilson/non-domain-specific/shift-helpers/core';
import {
  IsShiftPublishedPipe,
  ToNumberOfShiftPerDayRecordPipe,
} from '@wilson/non-domain-specific/shift-helpers/pipes';
import { GetPublicHolidayPipe } from '@wilson/organizational-unit';
import { TimelinePageFrameComponent } from '@wilson/page-frame';
import { PayrollTransactionsComponent } from '@wilson/payroll/shared';
import {
  MinutesToAverageHoursPipe,
  MinutesToHHmmDurationPipe,
  PipesModule,
} from '@wilson/pipes';
import { UserTimelinesProfitabilityScoreComponent } from '@wilson/profitability-score/core';
import {
  EmptyStateModule,
  LoadingSpinnerModule,
  SharedComponentsModule,
  UserDetailsModule,
} from '@wilson/shared-components';
import {
  AbsenceViewCardComponent,
  AssignmentShiftCardComponent,
  DatesDemarcatorLinesComponent,
  DeferredAbsenceCellViewCardComponent,
  DeferredAbsenceTimelineViewCardComponent,
  DeferredAssignmentShiftCardCellViewComponent,
  DeferredAssignmentShiftCardComponent,
  DeferredClusterCardComponent,
  DeferredShiftIconsComponent,
  DeferredStayCardComponent,
  DeferredUnassignedShiftCardCellViewComponent,
  GanttChartDayDemarcatorComponent,
  GanttChartTimeDifferenceComponent,
  GanttDateToPositionPxPipe,
  GanttDayLeftPipe,
  GanttDayRightPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  ShiftCardComponent,
  ShiftCardTooltipComponent,
  ShiftTimelineDateHeaderComponent,
  ShiftTimelineVirtualScrollStrategyDirective,
  ShiftValidationTooltipWrapperComponent,
  TabOptionComponent,
  TimeDifferenceWidthPxPipe,
  TimeLineAbsenceTooltipComponent,
  TimeLineActivityTooltipComponent,
  TimeLineShiftTooltipComponent,
  ToDateTimeRangePipe,
  UnassignedRegionRegularViewCollapsedStateRenderComponent,
  UnassignedRegionResizeHandleComponent,
  UnassignedRegionSegmentComponent,
  UnassignedRegionToggleComponent,
  UnassignedShiftWithoutActivitiesOverlapLevelsPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttIsActivityEndLocationMismatch,
  GanttIsActivityStartLocationMismatch,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import { ShiftValidationsCoreModule } from '@wilson/shift-validations/core';
import {
  ShiftConfirmedDeclinedComponent,
  ShiftCoreModule,
  ShiftLastSeenComponent,
} from '@wilson/shift/core';
import { OvertimeValueComponent } from '@wilson/work-time-management';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzElementPatchModule } from 'ng-zorro-antd/core/element-patch';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { DateFnsModule } from 'ngx-date-fns';
import { NgxEditorModule } from 'ngx-editor';
import { AssignShiftDropZoneV2Component } from './components-v2/assign-shift-drop-zone-v2/assign-shift-drop-zone-v2.component';
import { TimelineAutoSizeUserShiftsV2Component } from './components-v2/timeline-auto-size-user-shifts-v2/timeline-auto-size-user-shifts-v2.component';
import { TimelineCellViewUserRowComponent } from './components-v2/timeline-cell-view-user-row/timeline-cell-view-user-row.component';
import { TimelineUserRowShiftsV2Component } from './components-v2/timeline-user-row-shifts-v2/timeline-user-row-shifts-v2.component';
import { TimelineUserRowV2Component } from './components-v2/timeline-user-row-v2/timeline-user-row-v2.component';
import { ActivityCardContentComponent } from './components/activity-card-content/activity-card-content.component';
import { ActivityDropZoneHelperService } from './components/activity-drop-zone/activity-drop-zone-helper.service';
import { ActivityGapFillAnimationComponent } from './components/activity-gap-fill-animation/activity-gap-fill-animation.component';
import { ActivityResetDropZoneComponent } from './components/activity-reset-zone/activity-reset-drop-zone.component';
import { DemarcatorLinesComponent } from './components/demarcator-lines/demarcator-lines.component';
import { DragDropModeActionBarComponent } from './components/drag-drop-mode-action-bar/drag-drop-mode-action-bar.component';
import { TimelineActivityDrawerComponent } from './components/drawers/TimelineActivityDrawer/timeline-activity-drawer.component';
import { MultipleShiftsDrawerComponent } from './components/drawers/multiple-shifts-drawer/multiple-shifts-drawer.component';
import { TimelineAbsenceDrawerComponent } from './components/drawers/timeline-absence-drawer/timeline-absence-drawer.component';
import { TimelineDrawerComponent } from './components/drawers/timeline-drawer/timeline-drawer.component';
import { TimelineCreateStaysDrawerComponent } from './components/drawers/timeline-stays/timeline-create-stays-drawer/timeline-create-stays-drawer.component';
import { TimelineStaysEditDrawerComponent } from './components/drawers/timeline-stays/timeline-stays-edit-drawer/timeline-stays-edit-drawer.component';
import { FilterQualifiedStaffChangedButtonComponent } from './components/filter-qualified-staff-changed-button/filter-qualified-staff-changed-button.component';
import { FilterQualifiedWorkingTimeStaffChangedButtonComponent } from './components/filter-qualified-working-time-staff-changed-button/filter-qualified-working-time-staff-changed-button.component';
import { GroupedActivityStaffStatusFilterComponent } from './components/grouped-activity-staff-status-filter/grouped-activity-staff-status-filter.component';
import { LoadingShiftCardComponent } from './components/loading-shift-card/loading-shift-card.component';
import { MoveAbsenceDropZoneComponent } from './components/move-absence-drop-zone/move-absence-drop-zone.component';
import { NewAbsenceDropZoneComponent } from './components/new-absence-drop-zone/new-absence-drop-zone.component';
import { NewAbsenceQuickActionCardComponent } from './components/new-absence-quick-action-card/new-absence-quick-action-card.component';
import { NewActivityQuickCreationCardComponent } from './components/new-activity-quick-creation-card/new-activity-quick-creation-card.component';
import { NewShiftQuickCreationCardComponent } from './components/new-shift-quick-creation-card/new-shift-quick-creation-card.component';
import { QuickCreationBarComponent } from './components/quick-creation-bar/quick-creation-bar.component';
import { ShiftQuickCreationFromTemplateCardComponent } from './components/shift-quick-creation-from-template-card/shift-quick-creation-from-template-card.component';
import { ShiftTemplateHelperService } from './components/shift-template-drop-zone/shift-template-helper.service';
import { ShiftTemplateSelectComponent } from './components/shift-template-select/shift-template-select.component';
import { ShiftTemplateSelectService } from './components/shift-template-select/shift-template-select.service';
import { IsDateHighlightedPipe } from './components/shift-timeline-date-picker/is-date-highlighted.pipe';
import { ShiftTimelineDatePickerComponent } from './components/shift-timeline-date-picker/shift-timeline-date-picker.component';
import { CellViewDrawersComponent } from './components/shift-timeline-gantt-chart/cell-view-drawers/cell-view-drawers.component';
import { MouseMovementHandlerDirective } from './components/shift-timeline-gantt-chart/mouse-movement-handler.directive';
import { ShiftTimelineGanttChartComponent } from './components/shift-timeline-gantt-chart/shift-timeline-gantt-chart.component';
import { StandardViewDrawersComponent } from './components/shift-timeline-gantt-chart/standard-view-drawers/standard-view-drawers.component';
import { ShiftTimelineGanttHeaderComponent } from './components/shift-timeline-gantt-header/shift-timeline-gantt-header.component';
import { ShiftTimelineHeaderComponent } from './components/shift-timeline-header/shift-timeline-header.component';
import { ShiftTimelineKPISettingButtonComponent } from './components/shift-timeline-kpi-setting-button/shift-timeline-kpi-setting-button.component';
import { ShiftTimelineComponent } from './components/shift-timeline/shift-timeline.component';
import { ResizeActivityHelperService } from './components/timeline-auto-size-shift-with-activities-card/TimelineAutoSizeActivityCard/resize-activity-helper.service';
import { TimelineAutoSizeShiftWithActivitiesCardComponent } from './components/timeline-auto-size-shift-with-activities-card/timeline-auto-size-shift-with-activities-card.component';
import { TimelineAutoSizeUserShiftsComponent } from './components/timeline-auto-size-user-shifts/timeline-auto-size-user-shifts.component';
import { TimelineContentLoaderComponent } from './components/timeline-content-loader/timeline-content-loader.component';
import { TimelineEmptyFilterStateComponent } from './components/timeline-empty-filters-state/timeline-empty-filters-state.component';
import { TimelineFilterComponent } from './components/timeline-filter/timeline-filter.component';
import { DisplayQualifiedWorkingTimeCheckboxComponent } from './components/timeline-filters/display-qualified-working-time-checkbox/display-qualified-working-time-checkbox.component';
import { ShowQualifedStaffCheckboxComponent } from './components/timeline-filters/show-qualifed-staff-checkbox/show-qualifed-staff-checkbox.component';
import { UserActivityAssignmentAvailabilityFilterComponent } from './components/timeline-filters/user-activity-assignment-availability-filter/user-activity-assignment-availability-filter.component';
import { StaysKpiComponent } from './components/timeline-header-kpi-section/stays-kpi/stays-kpi.component';
import { TimelineHeaderKPISectionComponent } from './components/timeline-header-kpi-section/timeline-header-kpi-section.component';
import { TimelineRenderActivitiesToggleComponent } from './components/timeline-render-activities-toggle/timeline-render-activities-toggle.component';
import { TimelineRenderStaysToggleComponent } from './components/timeline-render-stays-toggle/timeline-render-stays-toggle.component';
import { TimelineShiftTimeTagComponent } from './components/timeline-shift-time-tag/timeline-shift-time-tag.component';
import { AssignAllShiftsWithoutActivitiesButtonComponent } from './components/timeline-unassigned-region/assign-all-shifts-without-activities-button/assign-all-shifts-without-activities-button.component';
import { CellViewTimelineUnassignedRegionComponent } from './components/timeline-unassigned-region/cell-view-timeline-unassigned-region/cell-view-timeline-unassigned-region.component';
import { RegularViewTimelineUnassignedRegionComponent } from './components/timeline-unassigned-region/regular-view-timeline-unassigned-region/regular-view-timeline-unassigned-region.component';
import { UnassignedActivitiesRowComponent } from './components/timeline-unassigned-region/unassigned-activities-row/unassigned-activities-row.component';
import { UnassignedServicesAndJobsVirtualScrollStrategyDirectiveDirective } from './components/timeline-unassigned-region/unassigned-activities-row/unassigned-services-and-jobs-virtual-scroll-strategy-directive.directive';
import { UnassignedRegionActivitySearchFilterComponent } from './components/timeline-unassigned-region/unassigned-region-activity-search-filter/unassigned-region-activity-search-filter.component';
import { UnassignedRegionSearchFilterComponent } from './components/timeline-unassigned-region/unassigned-region-search-filter/unassigned-region-search-filter.component';
import { UnassignedShiftWithoutActivitiesTabComponent } from './components/timeline-unassigned-region/unassigned-shift-without-activities-tab/unassigned-shift-without-activities-tab.component';
import { TimelineUserAbsenceComponent } from './components/timeline-user-absence/timeline-user-absence.component';
import { TimelineZoomLevelComponent } from './components/timeline-zoom-level/timeline-zoom-level.component';
import { UnassignedServicesAndJobsRowComponent } from './components/unassigned-services-and-jobs-row/unassigned-services-and-jobs-row.component';
import { UserTimelinesInfoCardComponent } from './components/user-timelines-info-card/user-timelines-info-card.component';
import { ActivitiesToAssignBarHeightPipe } from './pipes/activities-to-assign-bar-height.pipe';
import { ToServiceFilterDataPipe } from './pipes/to-service-filter-data.pipe';
import { DragAndDropHelperService } from './services/drag-and-drop-helper.service';
import { GanttActivityDragHandlerHelperService } from './services/gantt-activity-drag-handler-helper.service';
import { GanttActivityResizeHandlerHelperService } from './services/gantt-activity-resize-handler-helper.service';
import { MouseMovementHelperService } from './services/mouse-movement-helper.service';
import { ShiftTimelineDraftHelperService } from './services/shift-timeline-draft-helper/shift-timeline-draft-helper.service';
import { TimelineModalFilterService } from './services/timeline-modal-filter.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from './services/timeline-shift-drag-and-drop-directive-helper.service';
import { ShiftTimelineRoutingModule } from './shift-timeline-routing.module';
import {
  SimplifiedOrDetailShiftWrapperComponent,
  ShiftFormEditButtonComponent,
  ShiftFormViewButtonComponent,
} from '@wilson/non-domain-specific/simple-shift-drawer';
import { ClientCommentsSectionComponent } from '@wilson/non-domain-specific/shift-client-comments';

@NgModule({
  declarations: [
    ShiftTimelineComponent,
    ShiftTimelineDatePickerComponent,
    TimelineZoomLevelComponent,
    ActivitiesToAssignBarHeightPipe,
    ShiftTimelineGanttChartComponent,
    ShiftTimelineGanttHeaderComponent,
    RegularViewTimelineUnassignedRegionComponent,
    TimelineDrawerComponent,
    NewShiftQuickCreationCardComponent,
    NewActivityQuickCreationCardComponent,
    TimelineHeaderKPISectionComponent,
    GroupedActivityStaffStatusFilterComponent,
    ShiftTimelineHeaderComponent,
    QuickCreationBarComponent,
    ShiftQuickCreationFromTemplateCardComponent,
    UnassignedActivitiesRowComponent,
    StandardViewDrawersComponent,
    CellViewDrawersComponent,
  ],
  providers: [
    ShiftOperationsService,
    IsShiftPublishedPipe,
    ActivityTimePipe,
    AblePurePipe,
    GanttIsActivityStartLocationMismatch,
    GanttIsActivityEndLocationMismatch,
    DragAndDropModeService,
    GanttActivityDragHandlerHelperService,
    GanttActivityResizeHandlerHelperService,
    TimelineModalFilterService,
    CreateNewViaDragAndDropService,
    ShiftTemplateHelperService,
    ActivityDropZoneHelperService,
    MouseMovementHelperService,
    GanttDateToPositionPxPipe,
    DragAndDropHelperService,
    ShiftTimelineDraftHelperService,
    MoveShiftHelperService,
    IsShiftSubmittedOrAcceptedPipe,
    CanAllShiftBeModifiedPipe,
    ResizeActivityHelperService,
    ShiftTemplateSelectService,
    TimelineShiftDragAndDropDirectiveHelperService,
  ],
  imports: [
    DeferredAbsenceCellViewCardComponent,
    DragDropModeActionBarComponent,
    CollapsibleActivitiesListComponent,
    ShiftTemplateSelectComponent,
    UnassignedServicesAndJobsVirtualScrollStrategyDirectiveDirective,
    UnassignedServicesAndJobsRowComponent,
    MoreShiftActionsDropdownComponent,
    AssignAllShiftsWithoutActivitiesButtonComponent,
    UnassignedShiftWithoutActivitiesTabComponent,
    GetPublicHolidayPipe,
    TimeLineAbsenceTooltipComponent,
    UnassignedRegionToggleComponent,
    UnassignedRegionResizeHandleComponent,
    TabOptionComponent,
    GanttEndingPixelPipe,
    GanttStartingPixelPipe,
    TimeDifferenceWidthPxPipe,
    GanttDayLeftPipe,
    GanttDayRightPipe,
    ShiftTimelineDateHeaderComponent,
    TimelineActivityDrawerComponent,
    ShiftTimelineVirtualScrollStrategyDirective,
    OpenFinalStaffButtonComponent,
    LoadingSpinnerModule,
    DemarcatorLinesComponent,
    NewAbsenceDropZoneComponent,
    AbsenceViewCardComponent,
    NewAbsenceQuickActionCardComponent,
    ShiftCardComponent,
    TimelineAutoSizeUserShiftsComponent,
    LoadingShiftCardComponent,
    ShiftTimelinePipesModule,
    TimelineAutoSizeShiftWithActivitiesCardComponent,
    ActivityCardContentComponent,
    TimeLineActivityTooltipComponent,
    NzElementPatchModule,
    ShiftValidationTooltipWrapperComponent,
    TimeLineShiftTooltipComponent,
    ShiftCardTooltipComponent,
    FeatureFlagsModule,
    CommonModule,
    ShiftTimelineRoutingModule,
    TranslateModule,
    SharedComponentsModule,
    ContentLoaderModule,
    NzTooltipModule,
    AbsencesModule,
    NzButtonModule,
    FontAwesomeModule,
    NzModalModule,
    FilterModule,
    NzDatePickerModule,
    NzRadioModule,
    FormsModule,
    PipesModule,
    NzSkeletonModule,
    NzDividerModule,
    NzTagModule,
    ShiftCoreModule,
    UserDetailsModule,
    DateFnsModule,
    ShiftValidationsCoreModule,
    ScrollingModule,
    NzTabsModule,
    NzDrawerModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    CalculateDurationComponent,
    CalculateWorkingHourComponent,
    ShiftStatusComponent,
    PriorityModule,
    ActivitiesPipesModule,
    AuthorizationModule,
    ActivityTimeTagComponent,
    NzCheckboxModule,
    TimelineAbsenceDrawerComponent,
    OvertimeValueComponent,
    FilterQualifiedStaffChangedButtonComponent,
    FilterQualifiedWorkingTimeStaffChangedButtonComponent,
    ShowQualifedStaffCheckboxComponent,
    DisplayQualifiedWorkingTimeCheckboxComponent,
    UserActivityAssignmentAvailabilityFilterComponent,
    LabelsModule,
    NzBadgeModule,
    NzPopconfirmModule,
    NzCollapseModule,
    MouseMovementHandlerDirective,
    ActivityResetDropZoneComponent,
    GanttChartTimeDifferenceComponent,
    GanttChartDayDemarcatorComponent,
    UserTimelinesInfoCardComponent,
    ActivityGapFillAnimationComponent,
    AssignShiftDropZoneV2Component,
    MoveAbsenceDropZoneComponent,
    TimelineUserRowV2Component,
    TimelineShiftTimeTagComponent,
    TimelineUserRowShiftsV2Component,
    TimelineUserAbsenceComponent,
    TimelineAutoSizeUserShiftsV2Component,
    ToDateTimeRangePipe,
    AssignmentShiftCardComponent,
    UnassignedShiftWithoutActivitiesOverlapLevelsPipe,
    EmptyStateModule,
    ShiftTimelineKPISettingButtonComponent,
    MinutesToHHmmDurationPipe,
    MinutesToAverageHoursPipe,
    CollapsibleShiftAssignedUserIdentityComponent,
    CollapsibleShiftWageDataComponent,
    CollapsibleShiftQuickCheckComponent,
    CollapsibleShiftFurtherInformationComponent,
    CollapsibleShiftNoteComponent,
    CollapsibleShiftAttachmentsComponent,
    CollapsibleShiftReportsAttachmentsComponent,
    UserTimelinesProfitabilityScoreComponent,
    PayrollTransactionsComponent,
    IsShiftSubmittedOrAcceptedPipe,
    LastUpdateStatusComponent,
    AcceptShiftButtonComponent,
    SendShiftBackToAssigneeButtonComponent,
    MultipleShiftsDrawerComponent,
    ShiftLastSeenComponent,
    ShiftConfirmedDeclinedComponent,
    NzInputModule,
    ShiftNameExistsTooltipComponent,
    NzSegmentedModule,
    UnassignedRegionSegmentComponent,
    RevertShiftButtonComponent,
    UnassignedRegionSearchFilterComponent,
    NzEmptyModule,
    ToServiceFilterDataPipe,
    UnassignedRegionActivitySearchFilterComponent,
    TimelineRenderActivitiesToggleComponent,
    TimelineContentLoaderComponent,
    TimelineCreateStaysDrawerComponent,
    StaysKpiComponent,
    TimelineStaysEditDrawerComponent,
    TimelineRenderStaysToggleComponent,
    SetShiftProjectComponent,
    CanAllShiftBeModifiedPipe,
    IsDateHighlightedPipe,
    TimelineCellViewUserRowComponent,
    CellViewTimelineUnassignedRegionComponent,
    TimelineFilterComponent,
    TimelineEmptyFilterStateComponent,
    CollapsibleShiftContentComponent,
    TimelinePageFrameComponent,
    DatesDemarcatorLinesComponent,
    ShiftFormViewButtonComponent,
    ShiftFormEditButtonComponent,
    NgxEditorModule,
    AblePipe,
    AblePurePipe,
    DeferredAssignmentShiftCardComponent,
    UnassignedRegionRegularViewCollapsedStateRenderComponent,
    ToNumberOfShiftPerDayRecordPipe,
    DeferredAssignmentShiftCardCellViewComponent,
    DeferredUnassignedShiftCardCellViewComponent,
    DeferredClusterCardComponent,
    DeferredStayCardComponent,
    DeferredShiftIconsComponent,
    DeferredAbsenceTimelineViewCardComponent,
    CollapsibleShiftDetailComponent,
    ShiftFormPublishButtonComponent,
    AuditTrailComponentsModule,
    NzDrawerHeaderComponent,
    NzDrawerContentComponent,
    DrawerSettingsManagerDirective,
    UseLocalizedDateFormatDirective,
    LocalizedDayMonthFormatPipe,
    LocalizedDateTimeFormatPipe,
    CollapsibleGroupingSectionComponent,
    ViewersAvatarsComponent,
    SimplifiedOrDetailShiftWrapperComponent,
    ClientCommentsSectionComponent,
  ],
  exports: [
    ShiftTimelineGanttChartComponent,
    GroupedActivityStaffStatusFilterComponent,
  ],
})
export class ShiftTimelineModule {}
